import { useEffect } from 'react';
import { BankIcon, FormattedMessageDiv, FormRadio, FormWrapper, LoadingAnimation, Navigation, useWizardContext, Card } from '@odmonorepo/core-components';
import { Card as BsCard, Col, Stack } from 'react-bootstrap';
import { getMerchantProfile, getPlaidInfo, updateLDRs, getLDR } from 'merchant-api/spiceroad';
import { useOptimizelyVariation, isOptimizelyVariationEnabled, PLAID_ACTIVE_CONNECTION_FLAG } from 'merchant-api/vendor/optimizely';
import { useAnalytics } from 'use-analytics';
import * as yup from 'yup';
import { useFormContext } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateBankInfoOpts } from 'merchant-api/checkout';

export const BankConfirmationSchema = yup.object().shape({
  bankConfirmation: yup.object().shape({
    checkingAccount: yup.string().required(),
  }),
});

export const BankConfirmationCard = () => {
  return (
    <FormWrapper schema={BankConfirmationSchema}>
      <BankConfirmationForm />
    </FormWrapper>
  );
};

const BankConfirmationForm = () => {
  const { nextStep, goToStep, getCurrentStep } = useWizardContext();
  const { data: plaidActiveConnectionVariation, isSuccess: plaidActiveConnectionResolved, isLoading: isLoadingPlaidActiveConnection } = useOptimizelyVariation({ featureKey: PLAID_ACTIVE_CONNECTION_FLAG, analytics: useAnalytics() });
  const { loanDocRequest, hasOpenBankRequest, ldrLoading, ldrError } = getLDR();
  const { bankName, accountNumber, applicationType } = getMerchantProfile();
  const queryClient = useQueryClient();
  const { mutateAsync: updateBankInfoMutation } = useMutation({
    ...updateBankInfoOpts(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['checkout'] });
      nextStep();
    },
  });
  const { mutation: plaidMutation } = getPlaidInfo();
  const { updateLDR, updateLDRsError, isUpdateLDRsLoading } = updateLDRs();
  const { watch } = useFormContext();
  const watchBankConfirmation = watch('bankConfirmation.checkingAccount');

  useEffect(() => {
    getCurrentStep() === 'BANK_CONFIRMATION' && ((plaidActiveConnectionResolved && !isOptimizelyVariationEnabled(plaidActiveConnectionVariation)) || applicationType === 'NEW' || hasOpenBankRequest === false) && nextStep();
  }, [plaidActiveConnectionVariation, plaidActiveConnectionResolved, applicationType, hasOpenBankRequest]);

  if (ldrLoading || isLoadingPlaidActiveConnection || isUpdateLDRsLoading)
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );

  if (ldrError || updateLDRsError) throw ldrError || updateLDRsError;
  const handleContinue = async () => {
    if (watchBankConfirmation === 'No') {
      sessionStorage.setItem('bankConfirmation', 'No');
      getCurrentStep() === 'BANK_CONFIRMATION' ? nextStep() : goToStep('UPDATE_BANK');
    }

    if (watchBankConfirmation === 'Yes') {
      sessionStorage.setItem('bankConfirmation', 'Yes');
      if (getCurrentStep() === 'BANK_CONFIRMATION') {
        const res = await plaidMutation.mutateAsync();
        const assetsRefreshOcrolusStatus = res?.assetsRefreshOcrolusStatus;
        const isPlaidMerchantInfoEmpty = res?.plaidMerchantInfo === null && res?.assetsRefreshOcrolusStatus === null;
        if (isPlaidMerchantInfoEmpty === false && assetsRefreshOcrolusStatus === 'SUCCESS' && loanDocRequest) {
          updateLDR([...loanDocRequest.map((col) => ({ id: col.id, status: 'NOT_REQUIRED' }))]);
          return goToStep('RECEIVED');
        }
        return goToStep('BANK_INFO');
      } else {
        await updateBankInfoMutation(undefined);
      }
    }
  };
  return (
    <Stack gap={6} style={{ maxWidth: '600px', margin: 'auto' }}>
      <FormattedMessageDiv className="h3 text-center" id="renewalApp.step.3" />
      <Card bodyClassName="px-md-8">
        <Stack gap={5}>
          <Stack>
            <h4>
              <FormattedMessageDiv id={'loanApp.step.bankConfirmation.heading'} />
            </h4>
            <FormattedMessageDiv id={'loanApp.step.bankConfirmation.subheading'} />
          </Stack>
          <Stack direction="vertical" gap={5} className="align-items-stretch">
            <Col>
              <BankDetailsCard bankName={bankName} accountNumber={accountNumber} />
            </Col>
            <Col>
              <FormRadio name="bankConfirmation.checkingAccount" data-testid="bankConfirmation.checkingAccount" label={null} inline={false} required={false} gap={2} options={['Yes', 'No']} />
            </Col>
          </Stack>
        </Stack>
      </Card>
      <Navigation continueOnly handleNext={handleContinue} />
    </Stack>
  );
};
export interface LOCCardProps {
  bankName: string | undefined;
  accountNumber: string | undefined;
}

const BankDetailsCard = ({ bankName, accountNumber }: LOCCardProps) => (
  <BsCard bg="light-gray" className="p-5 shadow-none w-100 rounded-3">
    <Stack direction="vertical" gap={5}>
      <BankIcon className="mx-auto" />
      <Stack gap={1} className="fs-7">
        <FormattedMessageDiv id="loanApp.step.bankConfirmation.bankName" values={{ bankName: bankName }} className="text-center fw-bold" />
        <FormattedMessageDiv id="loanApp.step.bankConfirmation.bankText" values={{ lastFour: accountNumber?.slice(-4) }} className="text-center" />
      </Stack>
    </Stack>
  </BsCard>
);

export default BankConfirmationCard;
